import { type BootPhase, BootPhaseEnum } from '@schemas/boot'
import { useReducedMotion } from 'motion/react'
import { useEffect, useRef, useState } from 'react'

// The gate is a setTimeout, not a motion delay: a splash that flashes on a warm cache feels
// slower than no splash at all, so the screen must not be in the DOM before it elapses.
export const BOOT_GATE_MS = 300
export const BOOT_MIN_HOLD_MS = 500
export const BOOT_EXIT_MS = 200
// Absolute ceiling. The loop never runs forever — past this the screen hands off to the
// connection error rather than implying Rally is still trying.
export const BOOT_TIMEOUT_MS = 20_000

// RAL-187: the TikTok callback exchanges the code and saves tokens server-side before it
// redirects, so by the time the browser lands there is nothing left "connecting" — what the user
// actually waits through is the SPA's own cold boot on the return. Label it for what it is
// finishing rather than the generic workspace restore.
export const bootLabel = (search: string): string =>
  new URLSearchParams(search).get('connected') === '1' ? 'Finishing TikTok connection' : 'Loading workspace'

export const useAppBoot = (isReady: boolean): BootPhase => {
  const [phase, setPhase] = useState<BootPhase>(BootPhaseEnum.enum.idle)
  const shownAt = useRef(0)
  // One boot per session: once this run has resolved, a later flap of `isReady` (a Clerk
  // re-check, a router re-init) must not replay the splash.
  const booted = useRef(false)
  const reduce = useReducedMotion()

  useEffect(() => {
    if (isReady || booted.current) return

    const gate = setTimeout(() => {
      shownAt.current = Date.now()
      setPhase(BootPhaseEnum.enum.visible)
    }, BOOT_GATE_MS)
    const ceiling = setTimeout(() => setPhase(BootPhaseEnum.enum.failed), BOOT_TIMEOUT_MS)

    return () => {
      clearTimeout(gate)
      clearTimeout(ceiling)
    }
  }, [isReady])

  useEffect(() => {
    if (!isReady || booted.current) return
    if (phase === BootPhaseEnum.enum.failed || phase === BootPhaseEnum.enum.exiting) return

    // Never crossed the gate, so nothing was ever rendered and there is nothing to unwind.
    if (phase === BootPhaseEnum.enum.idle) {
      booted.current = true
      return
    }

    const remaining = BOOT_MIN_HOLD_MS - (Date.now() - shownAt.current)
    if (remaining <= 0) {
      setPhase(BootPhaseEnum.enum.exiting)
      return
    }

    if (phase !== BootPhaseEnum.enum.held) setPhase(BootPhaseEnum.enum.held)
    const hold = setTimeout(() => setPhase(BootPhaseEnum.enum.exiting), remaining)
    return () => clearTimeout(hold)
  }, [isReady, phase])

  useEffect(() => {
    if (phase !== BootPhaseEnum.enum.exiting) return

    // Reduced motion gets an instant swap, not a gentler fade.
    const settle = setTimeout(
      () => {
        booted.current = true
        setPhase(BootPhaseEnum.enum.idle)
      },
      reduce ? 0 : BOOT_EXIT_MS
    )

    return () => clearTimeout(settle)
  }, [phase, reduce])

  return phase
}
